import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import './OrganizationSetup.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const OrganizationSetup: React.FC = () => {
  const { user, setOrganization, refreshOrganization } = useAuth();
  const navigate = useNavigate();
  
  const [step, setStep] = useState<'choice' | 'create' | 'join'>('choice');
  const [orgName, setOrgName] = useState('');
  const [inviteCode, setInviteCode] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleCreateOrganization = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!orgName.trim()) {
      setError('Organization name is required');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Create the organization
      const createResponse = await fetch(`${API_URL}/organizations`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: orgName.trim() }),
      });

      if (!createResponse.ok) {
        const errData = await createResponse.json();
        throw new Error(errData.detail || 'Failed to create organization');
      }

      const newOrg = await createResponse.json();

      // Add the current user as admin of the organization
      if (user?.email) {
        await fetch(`${API_URL}/organizations/${newOrg.id}/invite`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            email: user.email,
            name: user.name,
            role: 'admin',
          }),
        });
      }

      setOrganization(newOrg);
      await refreshOrganization();
      
      // Redirect to dashboard
      navigate('/admin');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create organization');
    } finally {
      setIsLoading(false);
    }
  };

  const handleJoinOrganization = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!inviteCode.trim()) {
      setError('Invite code or organization slug is required');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Try to find organization by slug
      const response = await fetch(`${API_URL}/organizations/by-slug/${inviteCode.trim()}`);
      
      if (!response.ok) {
        throw new Error('Organization not found. Please check the invite code.');
      }

      const org = await response.json();

      // Request to join (in a real app, this would send an invite request)
      if (user?.email) {
        const joinResponse = await fetch(`${API_URL}/organizations/${org.id}/invite`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            email: user.email,
            name: user.name,
            role: 'employee', // Default to employee when joining
          }),
        });

        if (!joinResponse.ok) {
          const errData = await joinResponse.json();
          throw new Error(errData.detail || 'Failed to join organization');
        }
      }

      setOrganization(org);
      await refreshOrganization();
      
      // Redirect to appropriate dashboard
      navigate(user?.role === 'admin' ? '/admin' : '/employee');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to join organization');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="org-setup-container">
      <div className="org-setup-card">
        <div className="org-setup-header">
          <h1>Welcome to Prepwise</h1>
          <p>Let's get you set up with your organization</p>
        </div>

        {step === 'choice' && (
          <div className="org-setup-choices">
            <button 
              className="choice-button create"
              onClick={() => setStep('create')}
            >
              <div className="choice-icon">🏢</div>
              <div className="choice-content">
                <h3>Create Organization</h3>
                <p>Set up a new organization for your company</p>
              </div>
            </button>

            <div className="choice-divider">
              <span>or</span>
            </div>

            <button 
              className="choice-button join"
              onClick={() => setStep('join')}
            >
              <div className="choice-icon">🔗</div>
              <div className="choice-content">
                <h3>Join Organization</h3>
                <p>Join an existing organization with an invite</p>
              </div>
            </button>
          </div>
        )}

        {step === 'create' && (
          <form onSubmit={handleCreateOrganization} className="org-setup-form">
            <button 
              type="button" 
              className="back-button"
              onClick={() => setStep('choice')}
            >
              ← Back
            </button>
            
            <h2>Create Your Organization</h2>
            
            <div className="form-group">
              <label htmlFor="orgName">Organization Name</label>
              <input
                type="text"
                id="orgName"
                value={orgName}
                onChange={(e) => setOrgName(e.target.value)}
                placeholder="Enter your company name"
                disabled={isLoading}
              />
            </div>

            {error && <div className="error-message">{error}</div>}

            <button 
              type="submit" 
              className="submit-button"
              disabled={isLoading || !orgName.trim()}
            >
              {isLoading ? 'Creating...' : 'Create Organization'}
            </button>
          </form>
        )}

        {step === 'join' && (
          <form onSubmit={handleJoinOrganization} className="org-setup-form">
            <button 
              type="button" 
              className="back-button"
              onClick={() => setStep('choice')}
            >
              ← Back
            </button>
            
            <h2>Join an Organization</h2>
            
            <div className="form-group">
              <label htmlFor="inviteCode">Organization Code</label>
              <input
                type="text"
                id="inviteCode"
                value={inviteCode}
                onChange={(e) => setInviteCode(e.target.value)}
                placeholder="Enter invite code or organization slug"
                disabled={isLoading}
              />
              <small>Ask your organization admin for the code</small>
            </div>

            {error && <div className="error-message">{error}</div>}

            <button 
              type="submit" 
              className="submit-button"
              disabled={isLoading || !inviteCode.trim()}
            >
              {isLoading ? 'Joining...' : 'Join Organization'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default OrganizationSetup;
